import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Gamepad2, Music, Zap, Crown, Medal, Rocket, Sparkles, Swords, Ghost } from "lucide-react";
import Navbar from "../components/Navbar";
import AuthSceneBackground from "../components/AuthSceneBackground";
import LandingPhoneMockup from "../components/LandingPhoneMockup";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MobileDownloadButton from "../components/MobileDownloadButton";
import useTopConvertingChannel from "../lib/useTopConvertingChannel";

// Featured banner promoting the Halloween 2026 event bundle. Fetches
// status on mount and hides itself when the event is closed OR the
// pilot already owns the bundle. Compact — designed to sit above the
// hero without pushing the primary CTAs below the fold.
function HalloweenLandingBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/halloween/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  if (!status || !status.available || status.owned) return null;
  const endsMs = new Date(status.ends_at).getTime();
  const remaining = Math.max(0, endsMs - Date.now());
  const days = Math.floor(remaining / 86400000);
  return (
    <Link
      to="/skins"
      className="relative block mb-6 border border-[#ff8a00] hover:border-[#ffd700] transition group"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,106,0,0.15) 0%, rgba(74,0,128,0.28) 55%, rgba(10,0,20,0.5) 100%)",
        boxShadow: "0 0 22px rgba(255,106,0,0.4)",
      }}
      data-testid="halloween-landing-banner"
    >
      <div className="flex flex-wrap items-center gap-4 px-4 py-3">
        <Ghost className="w-6 h-6 text-[#ffe066] group-hover:animate-pulse flex-shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffb648]">
            // LIMITED · HALLOWEEN 2026 · Ends Oct 31
          </div>
          <div className="font-heading font-black uppercase text-base sm:text-lg tracking-tight text-white">
            Halloween Bundle · Comet + Pumpkin Paddle + Pumpkin Bombs — ${status.price_usd.toFixed(2)}
          </div>
        </div>
        <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700] flex-shrink-0">
          {days} days left →
        </div>
      </div>
    </Link>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const loggedIn = user && user !== false;
  // Auto-promote the highest-converting channel — pulls the lifetime
  // funnel and returns `{src, conv, ...}` when one channel has ≥10
  // clicks AND ≥50% lift over the next channel. Null otherwise.
  const topChannel = useTopConvertingChannel();
  // Detect whether the pilot has the PWA installed on this device. We
  // consider the app "installed" when:
  //   - localStorage flag was set by the `appinstalled` listener in
  //     index.js (most reliable cross-browser signal)
  //   - OR Chromium's `getInstalledRelatedApps()` reports a hit (only
  //     fires when a Play Store-published TWA matching the manifest
  //     `related_applications` list is present — covers Play installs)
  // We also gate on NOT being already in standalone — the deep-link CTA
  // would be silly if we're already running inside the launcher.
  const [installedAndOutside, setInstalledAndOutside] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    if (isStandalone) return;  // already in app — no need to advertise
    let cancel = false;
    const check = async () => {
      let installed = false;
      try {
        if (window.localStorage.getItem("pwa-installed") === "1") {
          installed = true;
        }
      } catch {
        /* private mode */
      }
      if (!installed && typeof navigator.getInstalledRelatedApps === "function") {
        try {
          const apps = await navigator.getInstalledRelatedApps();
          if (Array.isArray(apps) && apps.length > 0) installed = true;
        } catch {
          /* unsupported / blocked */
        }
      }
      if (!cancel && installed) setInstalledAndOutside(true);
    };
    check();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="starfield" />
      <AuthSceneBackground />
      <Navbar />

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 fade-in">
        {loggedIn && <HalloweenLandingBanner />}
        {/* Hero */}
        <section className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-cosmic text-xs tracking-[0.4em] text-[#00ffff] uppercase mb-6">
              // ARCADE • SPACE • BRICK BREAKER
            </div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.9]">
              <span className="text-white">Unleash the</span>
              <br />
              <span className="text-[#b026ff]" style={{ textShadow: "0 0 24px rgba(176,38,255,0.5)" }}>
                Cosmic
              </span>{" "}
              <span className="text-[#00ffff]" style={{ textShadow: "0 0 24px rgba(0,255,255,0.4)" }}>
                Worm
              </span>
            </h1>
            <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
              Pilot a purple worm through <span className="text-[#00ffff] font-bold">3,000+ procedural levels</span> across 6 sectors of rotating block fortresses. Grab cosmic power-ups,
              chase high scores, survive the void.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              {topChannel && (
                <div
                  className="basis-full -mb-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700] flex items-center gap-2"
                  data-testid="hero-top-channel-badge"
                >
                  <span className="inline-block w-2 h-2 bg-[#ffd700] animate-pulse" />
                  // FEATURED · {topChannel.src.toUpperCase()} pilots install at {topChannel.conv}% (top channel)
                </div>
              )}
              {installedAndOutside && (
                <a
                  href="/?launch=pwa#open"
                  className="btn-neon"
                  data-testid="open-in-app-btn"
                  title="Open in the installed Cosmic Worm Breaker app"
                  onClick={(e) => {
                    // Best-effort deep link: navigating to the manifest's
                    // `start_url` in this browser tab won't switch into the
                    // standalone window, but it WILL be intercepted by the
                    // OS launcher on Android+iOS (which routes matching
                    // URLs to the installed PWA when available). On desktop
                    // Chrome it's a one-tap into the launcher window.
                    e.currentTarget.classList.add("opacity-60");
                  }}
                >
                  <span className="inline-flex items-center gap-3">
                    <Rocket className="w-4 h-4" /> Open in App
                  </span>
                </a>
              )}
              {loggedIn ? (
                <Link to="/levels" className="btn-neon" data-testid="hero-play-btn">
                  <span className="inline-flex items-center gap-3">
                    Launch Run <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn-neon" data-testid="hero-signup-btn">
                    <span className="inline-flex items-center gap-3">
                      Start Mission <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                  <Link to="/login" className="btn-neon cyan" data-testid="hero-login-btn">
                    Login
                  </Link>
                </>
              )}
              <MobileDownloadButton
                className="ml-0 sm:ml-1"
                testId="hero-download-btn"
              />
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
              <Stat label="Levels" value="3K+" color="#00ffff" />
              <Stat label="Power-ups" value="06" color="#ffd700" />
              <Stat label="Difficulty" value="∞" color="#ff007f" />
            </div>
          </div>

          {/* Visual panel — phone mockup that doubles as a "this works
              on your phone, install it" cue. Replaces the previous flat
              cyan glass panel. */}
          <div className="lg:col-span-5">
            <LandingPhoneMockup />
          </div>
        </section>

        {/* Top pilots — social proof */}
        <TopPilots />

        {/* Recent matches — fresh bracket finishes serve as live social
            proof "pilots are playing RIGHT NOW". One-tap Watch CTA deep-
            links into /replay/<id> for the OG-rich spectator surface. */}
        <RecentMatches />

        {/* Pilot's artifact progress — surfaces upgrade headroom right
            after the social proof so engaged pilots see X/6 owned at a
            glance with a one-tap path to /artifacts. Hidden for guests. */}
        {loggedIn && <ArtifactProgressCard />}

        {/* Revenge Match — appears only when the pilot has a clear
            nemesis (a bot they've lost to more than they've beaten).
            Card auto-hides for new pilots and pilots with no losses. */}
        {loggedIn && <RevengeMatchCard />}

        {/* Feature strip */}
        <section className="mt-24 grid md:grid-cols-3 gap-6">
          <Feature
            icon={<Gamepad2 className="w-5 h-5" />}
            title="Keyboard + Mouse"
            desc="Arrow keys or mouse control your spaceship paddle with pixel precision."
            color="#00ffff"
          />
          <Feature
            icon={<Zap className="w-5 h-5" />}
            title="6 Power-ups"
            desc="Multi-ball, Enlarge, Slow-mo, Extra Life, Wormhole Warp & Black Hole — stack them."
            color="#ffd700"
          />
          <Feature
            icon={<Music className="w-5 h-5" />}
            title="Retro Audio"
            desc="Synthesized arcade sounds and looping space music."
            color="#b026ff"
          />
        </section>
      </main>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="glass p-4 min-w-0" data-testid={`stat-${label.toLowerCase()}`}>
      <div
        className="font-heading font-black text-2xl sm:text-3xl truncate leading-none"
        style={{ color }}
      >
        {value}
      </div>
      <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">
        {label}
      </div>
    </div>
  );
}

function Feature({ icon, title, desc, color }) {
  return (
    <div className="glass p-6 hover:border-white/20 transition">
      <div
        className="w-10 h-10 border flex items-center justify-center mb-4"
        style={{ borderColor: color, color, boxShadow: `0 0 12px ${color}33` }}
      >
        {icon}
      </div>
      <div className="font-heading uppercase tracking-wider text-white mb-2">{title}</div>
      <p className="text-sm text-white/60 leading-relaxed">{desc}</p>
    </div>
  );
}

const RANK_STYLE = [
  { color: "#ffd700", glow: "0 0 14px rgba(255,215,0,0.35)", icon: Crown, label: "1ST" },
  { color: "#e0e7ff", glow: "0 0 12px rgba(224,231,255,0.3)", icon: Medal, label: "2ND" },
  { color: "#ff9b5c", glow: "0 0 10px rgba(255,155,92,0.25)", icon: Medal, label: "3RD" },
];

function TopPilots() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancel = false;
    api
      .get("/leaderboard/public-top")
      .then((r) => !cancel && setItems(r.data.items || []))
      .catch(() => !cancel && setItems([]));
    return () => {
      cancel = true;
    };
  }, []);

  if (items == null) return null;
  if (items.length === 0) return null;

  return (
    <section className="mt-24" data-testid="landing-top-pilots">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#ffd700] uppercase mb-2">
            // TOP PILOTS · THIS MONTH
          </div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl text-white">
            Leaderboard
          </h2>
        </div>
        <Link
          to="/login"
          className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-[#00ffff]"
          data-testid="landing-top-pilots-cta"
        >
          Join the board →
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {items.slice(0, 3).map((p, i) => {
          const s = RANK_STYLE[i] || RANK_STYLE[2];
          const Icon = s.icon;
          return (
            <div
              key={i}
              className="glass p-5 border flex flex-col gap-2"
              style={{ borderColor: `${s.color}66`, boxShadow: s.glow }}
              data-testid={`landing-top-pilot-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.35em] uppercase"
                  style={{ color: s.color }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s.label}
                </span>
                <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
                  Pilot
                </span>
              </div>
              <div
                className="font-heading font-black uppercase text-xl tracking-tight truncate"
                style={{ color: s.color }}
              >
                {p.name}
              </div>
              <div className="font-mono-cosmic text-xs tracking-widest text-white/60">
                {p.score.toLocaleString()} <span className="text-white/40">PTS</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


// --- RecentMatches ----------------------------------------------------
// Fresh bracket finishes for the landing "Recent Matches" strip. No auth
// required — public social-proof surface so first-time visitors see real
// pilots winning right now. Falls back to a quiet null if the endpoint
// is empty (first-day deploys) so we never show a sad "no matches" card.
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function RecentMatches() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancel = false;
    api
      .get("/tournaments/recent-finishes?limit=3")
      .then((r) => !cancel && setItems(r.data?.finishes || []))
      .catch(() => !cancel && setItems([]));
    return () => { cancel = true; };
  }, []);
  if (items == null) return null;
  if (items.length === 0) return null;
  return (
    <section className="mt-24" data-testid="landing-recent-matches">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#3aa8ff] uppercase mb-2">
            // RECENT MATCHES · LIVE BRACKETS
          </div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl text-white">
            Just Played
          </h2>
        </div>
        <Link
          to="/tournaments"
          className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-[#3aa8ff]"
          data-testid="landing-recent-matches-cta"
        >
          Enter the arena →
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {items.slice(0, 3).map((m) => (
          <Link
            key={m.id}
            to={`/replay/${m.id}`}
            className="glass p-5 border border-white/10 hover:border-[#3aa8ff]/60 transition flex flex-col gap-3"
            style={{ boxShadow: "0 0 16px rgba(58,168,255,0.08)" }}
            data-testid={`landing-recent-match-${m.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.35em] uppercase text-[#3aa8ff]">
                <Swords className="w-3.5 h-3.5" />
                LVL {String(m.level).padStart(2, "0")}
              </span>
              <span
                className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40"
                data-testid={`landing-recent-match-${m.id}-time`}
              >
                {timeAgo(m.completed_at) || `${m.size}-pilot`}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Crown className="w-4 h-4 text-[#ffd700] flex-shrink-0" />
              <span className="font-heading font-black uppercase tracking-tight text-lg truncate text-white">
                {m.winner_name}
              </span>
              {m.winner_is_bot ? (
                <span
                  className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40 border border-white/15 px-1.5 py-0.5"
                  data-testid={`landing-recent-match-${m.id}-bot-badge`}
                >
                  BOT
                </span>
              ) : (
                <span
                  className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-[#ffd700] border border-[#ffd700]/50 bg-[#ffd700]/5 px-1.5 py-0.5"
                  style={{ boxShadow: "0 0 8px rgba(255,215,0,0.2)" }}
                  data-testid={`landing-recent-match-${m.id}-human-badge`}
                  title="Real pilot — not a bot"
                >
                  HUMAN
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono-cosmic text-xs tracking-widest text-white/60 tabular-nums">
                {Number(m.winner_score || 0).toLocaleString()}
                <span className="text-white/40"> PTS</span>
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#3aa8ff]">
                Watch <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}


function ArtifactProgressCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/artifacts");
        if (cancelled) return;
        const cat = r.data?.catalog || [];
        const user = r.data?.user || {};
        const owned = (user.owned || []).length;
        const maxLevel = r.data?.max_level || 5;
        const maxed = (user.owned || []).filter(
          (id) => (user.levels || {})[id] >= maxLevel
        ).length;
        const total = cat.length || 6;
        // Suggest the cheapest next move: an unowned artifact (300 ☾)
        // or the lowest-level owned one to upgrade.
        let nextLabel = "All maxed · legendary tier complete";
        let nextHint = "";
        if (owned < total) {
          const next = cat.find((a) => !(user.owned || []).includes(a.id));
          if (next) {
            nextLabel = `Unlock ${next.name}`;
            nextHint = `${next.cost_buy} ☾`;
          }
        } else if (maxed < total) {
          // Find lowest-level owned artifact.
          const ranked = (user.owned || [])
            .map((id) => ({ id, lvl: (user.levels || {})[id] || 1 }))
            .sort((a, b) => a.lvl - b.lvl);
          const lowest = ranked[0];
          const lowestArt = cat.find((a) => a.id === lowest.id);
          const upgIdx = (lowest.lvl || 1) - 1;
          if (lowestArt && lowestArt.cost_upgrade?.[upgIdx] != null) {
            nextLabel = `Upgrade ${lowestArt.name} → L${lowest.lvl + 1}`;
            nextHint = `${lowestArt.cost_upgrade[upgIdx]} ☾`;
          }
        }
        setData({ owned, maxed, total, nextLabel, nextHint });
      } catch {
        /* noop */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const ownedPct = (data.owned / data.total) * 100;
  const maxedPct = (data.maxed / data.total) * 100;

  return (
    <section
      className="mt-16"
      aria-labelledby="artifacts-progress-heading"
      data-testid="landing-artifact-progress"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2
          id="artifacts-progress-heading"
          className="font-heading text-xl tracking-tight text-white"
        >
          <span className="text-[#b026ff]">//</span> Artifact Progress
        </h2>
        <Link
          to="/artifacts"
          className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#b026ff] hover:text-white"
          data-testid="landing-artifact-cta"
        >
          Visit Artifacts →
        </Link>
      </div>
      <div className="glass p-5 sm:p-6 border border-[#b026ff]/35">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-center">
          <div className="space-y-3 min-w-0">
            <ProgressBar
              label="Owned"
              value={data.owned}
              total={data.total}
              pct={ownedPct}
              color="#b026ff"
              testId="artifact-progress-owned"
            />
            <ProgressBar
              label="Maxed (L5)"
              value={data.maxed}
              total={data.total}
              pct={maxedPct}
              color="#ffd700"
              testId="artifact-progress-maxed"
            />
          </div>
          <Link
            to="/artifacts"
            className="block px-4 py-3 border border-[#b026ff]/55 hover:border-[#b026ff] hover:bg-[#b026ff]/10 transition group"
            data-testid="artifact-progress-next-cta"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[#ffd700]" />
              <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/60">
                Next move
              </span>
            </div>
            <div className="font-mono-cosmic text-xs tracking-[0.05em] text-white truncate max-w-[260px]">
              {data.nextLabel}
            </div>
            {data.nextHint && (
              <div className="font-mono-cosmic text-[10px] tabular-nums text-[#ffd700] mt-1">
                {data.nextHint}
              </div>
            )}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProgressBar({ label, value, total, pct, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/65">
          {label}
        </span>
        <span
          className="font-mono-cosmic text-xs tabular-nums"
          style={{ color }}
        >
          {value} <span className="text-white/40">/ {total}</span>
        </span>
      </div>
      <div className="h-1.5 bg-white/8 overflow-hidden rounded-sm">
        <div
          className="h-full transition-[width] duration-700"
          style={{
            width: `${Math.min(100, Math.max(0, pct))}%`,
            backgroundColor: color,
            boxShadow: `0 0 8px ${color}aa`,
          }}
        />
      </div>
    </div>
  );
}

function RevengeMatchCard() {
  const [nemeses, setNemeses] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/bots/nemeses");
        if (cancelled) return;
        setNemeses(r.data.nemeses || []);
      } catch {
        setNemeses([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't render until loaded; auto-hide for pilots with no nemeses.
  if (!nemeses || nemeses.length === 0) return null;
  const top = nemeses[0];

  const startRevenge = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/bots/revenge-match", {
        bot_id: top.user_id, level: 1,
      });
      toast.success(`Revenge bracket vs ${r.data.bot_name}`, {
        description: "You're up first — make it count.",
        duration: 4500,
      });
      navigate(`/tournaments/${r.data.tournament_id}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't create the bracket"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-12"
      aria-labelledby="revenge-match-heading"
      data-testid="landing-revenge-match"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2
          id="revenge-match-heading"
          className="font-heading text-xl tracking-tight text-white"
        >
          <span className="text-[#ff007f]">//</span> Revenge Match
        </h2>
        <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/45">
          Settle the score · 5 ☾
        </div>
      </div>
      <div className="glass p-5 sm:p-6 border border-[#ff007f]/35 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-center">
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ff007f]/85">
            <Swords className="w-3.5 h-3.5" />
            Your nemesis
          </div>
          <div className="font-heading text-2xl sm:text-3xl text-white truncate">
            {top.name}
            <span className="text-white/30 text-base ml-1">#{top.tag}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 font-mono-cosmic text-[11px] tabular-nums">
            <span className="text-white/55">
              Head-to-head: <span className="text-[#28e070]">{top.wins}W</span>
              <span className="text-white/40">–</span>
              <span className="text-[#ff5577]">{top.losses}L</span>
            </span>
            <span className="text-white/30">·</span>
            <span className="text-[#ff007f]">
              You're down {top.deficit} match{top.deficit === 1 ? "" : "es"}
            </span>
          </div>
          {nemeses.length > 1 && (
            <div className="font-mono-cosmic text-[10px] tracking-[0.1em] text-white/40 pt-1">
              Also losing to:{" "}
              {nemeses.slice(1).map((n, i) => (
                <span key={n.user_id}>
                  <span className="text-white/65">{n.name}</span>{" "}
                  <span className="text-[#ff5577]">({n.wins}-{n.losses})</span>
                  {i < nemeses.length - 2 ? ", " : ""}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={startRevenge}
          disabled={busy}
          className="px-5 py-3 border-2 border-[#ff007f] bg-[#ff007f]/10 hover:bg-[#ff007f]/20 transition group disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="revenge-match-start-btn"
        >
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ff007f]/80 mb-0.5">
            {busy ? "Spawning bracket…" : "Challenge"}
          </div>
          <div className="font-heading text-base text-white tracking-tight">
            {busy ? "…" : "START REVENGE →"}
          </div>
          <div className="font-mono-cosmic text-[9px] tabular-nums text-white/45 mt-0.5">
            4-pilot bracket · 5 ☾ entry
          </div>
        </button>
      </div>
    </section>
  );
}

